import React from "react";
import { ForceGraphMethods } from "react-force-graph-2d";
import { TCanvasNode } from "../types/TCanvasNode";
import { TCanvasLink } from "../types/TCanvasLink";
import { TCanvasOptions } from "../types/TCanvasOptions";
import { cleanCircularLayout } from "../utils/d3Force/cleanCircularLayout";
import * as d3 from "d3-force";

/**
 * Clean Force Tweak Hook - Rebuilt from scratch
 * 
 * Uses pure mathematical circular positioning with gentle snap-back forces
 * No complex charge/repulsion - just clean hierarchical circles
 */
export default function useForceTweak(canvasOptions: TCanvasOptions | null, physicsParams?: any) {
  const fgRef = React.useRef<ForceGraphMethods<TCanvasNode, TCanvasLink>>();
  const zoomInitialized = React.useRef(false); // Track if zoom has been set
  
  // Use physics params or defaults
  const params = physicsParams || {
    domainSpacing: 800,
    tableRadius: 200,
    columnRadius: 80,
    snapBackStrength: 0.15,
    tableForceStrength: 0.1,   // Minimum for smoothest movement
    columnForceStrength: 0.4,
    velocityDecay: 0.6,
    collisionStrength: 0.3,
  };

  React.useEffect(() => {
    if (fgRef.current) {
      // REMOVE ALL OLD FORCES - Start clean
      fgRef.current.d3Force("charge", null);
      fgRef.current.d3Force("customCharge", null);
      fgRef.current.d3Force("customCenterAlign", null);
      fgRef.current.d3Force("collide", null);
      fgRef.current.d3Force("circularLayout", null);
      fgRef.current.d3Force("domainSeparation", null);
      
      // DISABLE default link force - we're using pure positioning
      fgRef.current.d3Force("link", null);
      
      // Add ONLY the clean circular layout force
      const circularForce = cleanCircularLayout()
        .center(0, 0)
        .domainSpacing(params.domainSpacing)
        .tableRadius(params.tableRadius)
        .columnRadius(params.columnRadius)
        .snapBackStrength(params.snapBackStrength)
        .enableSnapBack(false)
        .tableForceStrength(params.tableForceStrength || 0.3)
        .columnForceStrength(params.columnForceStrength || 0.4);
      
      fgRef.current.d3Force("cleanCircular", circularForce);
      
      // Add gentle collision to prevent overlaps (user-adjustable)
      fgRef.current.d3Force("collide", 
        d3.forceCollide((node: TCanvasNode) => {
          if (node.node_type === "DOMAIN") return 120;
          if (node.node_type === "TABLE") return 100;
          if (node.node_type === "COLUMN" || node.node_type === "OTHER") return 8;
          return 10;
        }).strength(params.collisionStrength || 0.25).iterations(2)
      );
      
      // Add gentle repulsion between tables/domains
      fgRef.current.d3Force("domainRepulsion", 
        d3.forceManyBody()
          .strength((node: TCanvasNode) => {
            if (node.node_type === "DOMAIN") return -60;
            if (node.node_type === "TABLE") return -45;
            return 0;
          })
          .distanceMax(600)
      );
      
      // Very weak centering force
      fgRef.current.d3Force("center", d3.forceCenter(0, 0).strength(0.005));
      
      // Velocity decay for smooth movement (user-adjustable)
      fgRef.current.d3Force("velocityDecay", null);
      if (fgRef.current.d3VelocityDecay) {
        fgRef.current.d3VelocityDecay(params.velocityDecay || 0.6);
      }
      
      // Warm up the simulation to apply forces immediately
      if (fgRef.current.d3ReheatSimulation) {
        fgRef.current.d3ReheatSimulation();
      }
      
      // Set initial zoom ONLY ONCE - don't override user's zoom preference
      if (!zoomInitialized.current && fgRef.current.zoom) {
        setTimeout(() => {
          if (fgRef.current && fgRef.current.zoom && !zoomInitialized.current) {
            fgRef.current.zoom(0.5, 400); // Initial zoom to 50% (more zoomed in)
            zoomInitialized.current = true; // Mark as initialized
          }
        }, 100);
      }
    }
  }, [fgRef, physicsParams]);

  return { fgRef };
}
